import { OSINT } from "../interfaces"

export interface QueryTypeMeta {
  type: OSINT.PayloadType
  label: string
  inputHelper: string
  placeholder: string
  validate: (line: string) => boolean
  parseLine: (line: string) => OSINT.Payload
  formatInput: (payload: OSINT.Payload) => string
}

const onlyDigits = (re: RegExp) => (s: string) => re.test(s)

export const QUERY_TYPES: QueryTypeMeta[] = [
  {
    type: "phone",
    label: "Телефон",
    inputHelper: "10–15 цифр без +. Пример: 79001234567",
    placeholder: "79001234567",
    validate: onlyDigits(/^\d{10,15}$/),
    parseLine: (line) => ({ type: "phone", phone: line }),
    formatInput: (p) => (p.type === "phone" ? p.phone : ""),
  },
  {
    type: "email",
    label: "Email",
    inputHelper: "Валидный email",
    placeholder: "user@example.com",
    validate: (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s),
    parseLine: (line) => ({ type: "email", email: line }),
    formatInput: (p) => (p.type === "email" ? p.email : ""),
  },
  {
    type: "person",
    label: "ФИО + ДР",
    inputHelper:
      "Формат: ФИО;ДД.ММ.ГГГГ. Пример: Иванов Иван Иванович;01.01.1980",
    placeholder: "Иванов Иван Иванович;01.01.1980",
    validate: (line) => {
      const parts = line.split(";").map((s) => s?.trim())
      return (
        parts.length === 2 &&
        !!parts[0] &&
        /^\d{2}\.\d{2}\.\d{4}$/.test(parts[1])
      )
    },
    parseLine: (line) => {
      const [fullName, dob] = line.split(";").map((s) => s.trim())
      return { type: "person", fullName, dob }
    },
    formatInput: (p) => (p.type === "person" ? `${p.fullName}, ${p.dob}` : ""),
  },
  {
    type: "vk",
    label: "ВКонтакте",
    inputHelper: "Username, @username или ссылка https://vk.com/username",
    placeholder: "durov или https://vk.com/durov",
    validate: (s) =>
      /^(?:https?:\/\/)?(?:www\.)?vk\.com\/([\w.]{2,})/.test(s) ||
      /^@?[\w.]{2,}$/.test(s),
    parseLine: (line) => {
      const urlMatch = line.match(
        /^(?:https?:\/\/)?(?:www\.)?vk\.com\/([\w.]+)/,
      )
      const username = urlMatch ? urlMatch[1] : line.replace(/^@/, "")
      return { type: "vk", username }
    },
    formatInput: (p) => (p.type === "vk" ? `@${p.username}` : ""),
  },
  {
    type: "tiktok",
    label: "TikTok",
    inputHelper: "Username TikTok",
    placeholder: "@charlidamelio",
    validate: (s) => /^@?[\w.]{2,}$/.test(s),
    parseLine: (line) => ({ type: "tiktok", username: line.replace(/^@/, "") }),
    formatInput: (p) => (p.type === "tiktok" ? `@${p.username}` : ""),
  },
  {
    type: "instagram",
    label: "Instagram",
    inputHelper: "Username Instagram",
    placeholder: "@instagram",
    validate: (s) => /^@?[\w.]{2,}$/.test(s),
    parseLine: (line) => ({
      type: "instagram",
      username: line.replace(/^@/, ""),
    }),
    formatInput: (p) => (p.type === "instagram" ? `@${p.username}` : ""),
  },
  {
    type: "ok",
    label: "Одноклассники",
    inputHelper: "Только цифры — ID профиля",
    placeholder: "501382263690",
    validate: onlyDigits(/^\d+$/),
    parseLine: (line) => ({ type: "ok", profileId: line }),
    formatInput: (p) => (p.type === "ok" ? p.profileId : ""),
  },
  {
    type: "telegram",
    label: "Telegram",
    inputHelper: "Username (@durov) или tgId (tg123456)",
    placeholder: "@durov",
    validate: (s) => /^@?\w{3,}$/.test(s) || /^tg\d+$/.test(s),
    parseLine: (line) =>
      /^tg\d+$/.test(line)
        ? { type: "telegram", tgId: line }
        : { type: "telegram", username: line.replace(/^@/, "") },
    formatInput: (p) => {
      if (p.type !== "telegram") return ""
      return p.username ? `@${p.username}` : (p.tgId ?? "")
    },
  },
  {
    type: "plate",
    label: "Гос. номер авто",
    inputHelper: "Непустая строка (например, А123БВ77)",
    placeholder: "А123БВ77",
    validate: (s) => s.length > 0,
    parseLine: (line) => ({ type: "plate", plate: line }),
    formatInput: (p) => (p.type === "plate" ? p.plate : ""),
  },
  {
    type: "vin",
    label: "VIN",
    inputHelper: "Ровно 17 символов, без I/O/Q",
    placeholder: "1HGCM82633A123456",
    validate: (s) => /^[A-HJ-NPR-Z0-9]{17}$/i.test(s),
    parseLine: (line) => ({ type: "vin", vin: line.toUpperCase() }),
    formatInput: (p) => (p.type === "vin" ? p.vin : ""),
  },
  {
    type: "passport",
    label: "Паспорт",
    inputHelper: "Ровно 10 цифр",
    placeholder: "4500123456",
    validate: onlyDigits(/^\d{10}$/),
    parseLine: (line) => ({ type: "passport", number: line }),
    formatInput: (p) => (p.type === "passport" ? p.number : ""),
  },
  {
    type: "vu",
    label: "Водительское удостоверение",
    inputHelper: "Ровно 10 цифр",
    placeholder: "7700123456",
    validate: onlyDigits(/^\d{10}$/),
    parseLine: (line) => ({ type: "vu", number: line }),
    formatInput: (p) => (p.type === "vu" ? p.number : ""),
  },
  {
    type: "snils",
    label: "СНИЛС",
    inputHelper: "Ровно 11 цифр",
    placeholder: "12345678901",
    validate: onlyDigits(/^\d{11}$/),
    parseLine: (line) => ({ type: "snils", number: line }),
    formatInput: (p) => (p.type === "snils" ? p.number : ""),
  },
  {
    type: "inn",
    label: "ИНН",
    inputHelper: "10 или 12 цифр",
    placeholder: "773119787842",
    validate: onlyDigits(/^\d{10}$|^\d{12}$/),
    parseLine: (line) => ({ type: "inn", number: line }),
    formatInput: (p) => (p.type === "inn" ? p.number : ""),
  },
  {
    type: "ogrn",
    label: "ОГРН",
    inputHelper: "13 или 15 цифр",
    placeholder: "1027700132195",
    validate: onlyDigits(/^\d{13}$|^\d{15}$/),
    parseLine: (line) => ({ type: "ogrn", number: line }),
    formatInput: (p) => (p.type === "ogrn" ? p.number : ""),
  },
  {
    type: "phonebook",
    label: "Телефонная книга",
    inputHelper: "Произвольный текстовый запрос",
    placeholder: "Иван фотограф",
    validate: (s) => s.length > 0,
    parseLine: (line) => ({ type: "phonebook", query: line }),
    formatInput: (p) => (p.type === "phonebook" ? p.query : ""),
  },
  {
    type: "address",
    label: "Адрес",
    inputHelper: "Произвольный адрес",
    placeholder: "г. Москва, ул. Тверская, 1",
    validate: (s) => s.length > 0,
    parseLine: (line) => ({ type: "address", address: line }),
    formatInput: (p) => (p.type === "address" ? p.address : ""),
  },
  {
    type: "cadastral",
    label: "Кадастровый номер",
    inputHelper: "Формат: 1:2:3456789:1234567",
    placeholder: "77:01:0001001:1234",
    validate: (s) => /^\d+:\d+:\d+:\d+$/.test(s),
    parseLine: (line) => ({ type: "cadastral", number: line }),
    formatInput: (p) => (p.type === "cadastral" ? p.number : ""),
  },
  {
    type: "domainOrIp",
    label: "Домен / IP",
    inputHelper: "Доменное имя или IP-адрес",
    placeholder: "example.com",
    validate: (s) => s.length > 0,
    parseLine: (line) => ({ type: "domainOrIp", value: line }),
    formatInput: (p) => (p.type === "domainOrIp" ? p.value : ""),
  },
]

export const QUERY_TYPE_BY_KEY: Record<OSINT.PayloadType, QueryTypeMeta> =
  Object.fromEntries(QUERY_TYPES.map((t) => [t.type, t])) as Record<
    OSINT.PayloadType,
    QueryTypeMeta
  >
